import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Eye, EyeOff, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";
import { toast } from "sonner";

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();

    const [password, setPassword] = useState("");
    const [confirmPw, setConfirmPw] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error("Heslo musi mit alespon 6 znaku"); return; }
        if (password !== confirmPw) { toast.error("Hesla se neshoduji"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/customer/reset-password", { token, new_password: password });
            setDone(true);
            toast.success(data.message);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Chyba pri obnove hesla");
        } finally { setLoading(false); }
    };

    if (!token) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: "var(--sg-bg-light)" }}>
                <Navbar />
                <div className="max-w-md mx-auto px-4 py-10 text-center">
                    <div className="bg-white border p-6" style={{ borderColor: "var(--sg-border)", borderRadius: "var(--sg-radius)" }}>
                        <h2 className="text-xl font-bold mb-2" style={{ color: "var(--sg-text)" }}>Neplatny odkaz</h2>
                        <p className="text-sm text-gray-500 mb-4">Odkaz na obnoveni hesla je neplatny nebo chybi token.</p>
                        <Link to="/zapomenute-heslo" className="text-sm font-medium hover:underline" style={{ color: "var(--sg-primary)" }}>
                            Pozadat o novy odkaz
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: "var(--sg-bg-light)" }}>
            <Navbar />
            <div className="max-w-md mx-auto px-4 py-10">
                <div className="bg-white border p-6" style={{ borderColor: "var(--sg-border)", borderRadius: "var(--sg-radius)" }}>
                    {done ? (
                        <div className="text-center space-y-4" data-testid="reset-pw-success">
                            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: "#f0fdf4" }}>
                                <CheckCircle size={24} style={{ color: "var(--sg-primary)" }} />
                            </div>
                            <h2 className="text-xl font-bold" style={{ color: "var(--sg-text)" }}>Heslo zmeneno</h2>
                            <p className="text-sm text-gray-500">Vase heslo bylo uspesne obnoveno. Nyni se muzete prihlasit.</p>
                            <Button onClick={() => navigate("/prihlaseni")} className="text-white" style={{ backgroundColor: "var(--sg-primary)" }}
                                data-testid="reset-pw-login-btn">
                                Prihlasit se
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-pw-form">
                            <div className="text-center mb-4">
                                <h2 className="text-xl font-bold" style={{ color: "var(--sg-text)" }}>Nove heslo</h2>
                                <p className="text-sm text-gray-500">Zadejte sve nove heslo</p>
                            </div>
                            <div>
                                <Label className="text-xs text-gray-600 mb-1">Nove heslo</Label>
                                <div className="relative">
                                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <Input value={password} onChange={e => setPassword(e.target.value)} type={showPw ? "text" : "password"}
                                        placeholder="Min. 6 znaku" className="pl-9 pr-9 h-10" required data-testid="reset-pw-password" />
                                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs text-gray-600 mb-1">Potvrzeni hesla</Label>
                                <div className="relative">
                                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <Input value={confirmPw} onChange={e => setConfirmPw(e.target.value)} type={showPw ? "text" : "password"}
                                        placeholder="Zopakujte heslo" className="pl-9 h-10" required data-testid="reset-pw-confirm" />
                                </div>
                            </div>
                            <Button type="submit" disabled={loading} className="w-full h-10 text-white" style={{ backgroundColor: "var(--sg-primary)" }}
                                data-testid="reset-pw-submit">
                                {loading ? "Menim heslo..." : "Zmenit heslo"} <ArrowRight size={14} className="ml-1" />
                            </Button>
                        </form>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}
